/**
 * ══════════════════════════════════════════════════════════════════════════════
 * **LA PEAU DES WIDGETS, ÉCRITE UNE FOIS.**
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠⚠ **ELLE ÉTAIT RECOPIÉE TROIS FOIS, À L'OCTET.** *`ClassicWidget`,
 *    `SmartWidget` et `FlashDirectWidget` portaient les **mêmes onze classes**
 *    et les **mêmes deux décorations d'état actif**, dupliquées. Mesuré avant
 *    d'extraire : les trois blocs sont identiques caractère pour caractère.
 *    **La demande de Hkm du 10/09** — « je veux que les 6 cadrans des notifs
 *    ressemblent aux widgets de la sidebar, même au clic ils deviennent bleus »
 *    — **en aurait fait une quatrième copie.** On importe, on ne recopie pas
 *    (`#453`).*
 *
 * ⚠ **CE MODULE NE DÉCIDE RIEN, IL DÉPLACE.** *Les classes sont reprises
 *   **telles quelles** : le rendu des trois widgets est inchangé, et leurs
 *   bancs le prouvent. Un « pendant qu'on y est » sur les teintes aurait
 *   mélangé une extraction et un changement de look — impossible à relire.*
 */
import type { JSX } from 'react';

/**
 * Forme, bordure, transition, anneau de focus.
 *
 * ⚠ *Découpé en deux morceaux pour que la carte de notification partage le
 *   `CADRE_ET_FOCUS` **sans** hériter du `h-full` d'un widget de sidebar : une
 *   carte de liste tient sa hauteur de son contenu.*
 */
const FORME = 'group relative rounded-xl px-2.5 py-2 text-left flex flex-col overflow-hidden';
const CADRE_ET_FOCUS = [
  'border transition-all duration-200',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50',
] as const;

export const PEAU_WIDGET_BASE = [`w-full h-full ${FORME}`, ...CADRE_ET_FOCUS] as const;

/** ⚠ **LE BLEU DE L'ÉTAT ACTIF** — l'accent Samsung, jamais un bleu inventé. */
export const PEAU_WIDGET_ACTIF = [
  'border-brand-samsung-500/70',
  'bg-gradient-to-b from-brand-samsung-500/[0.22] to-brand-samsung-500/[0.08]',
  'shadow-[0_0_0_1px_rgba(20,40,160,0.18),0_4px_20px_rgba(20,40,160,0.28)]',
] as const;

/**
 * ⚠ Le repos vient des TOKENS de surface — pas d'une teinte `slate-*` choisie.
 * ⚠ CLAIR SEULEMENT — contour bleu Samsung (canon du dashboard) en clair ; le
 *   token d'origine reste le bord de repos en sombre. Cette peau est partagée
 *   par tous les widgets/cards : un seul geste, tout le dashboard suit.
 */
export const PEAU_WIDGET_REPOS = [
  'border-[var(--wrapper-contour)] dark:border-[var(--border-default,var(--surface-border))]',
  'bg-gradient-to-b from-[var(--surface-elevated)] to-[var(--surface-bg)]',
] as const;

/**
 * ⚠⚠ **LE SURVOL EST SÉPARÉ, ET CE N'EST PAS DU CONFORT.** *`DetectionPatterns`
 *    — la **cinquième** copie de cette peau, trouvée par le banc — ne survole
 *    que si la tuile est `interactive`. Sans cette séparation il aurait fallu
 *    soit recopier la peau une fois de plus, soit lui donner un survol qui ment
 *    sur une tuile qu'on ne peut pas cliquer.*
 */
export const PEAU_WIDGET_SURVOL =
  'hover:border-brand-samsung-500/40 hover:bg-brand-samsung-500/[0.06] hover:shadow-[0_2px_14px_rgba(20,40,160,0.14)]';

export const PEAU_WIDGET_INACTIF = [...PEAU_WIDGET_REPOS, PEAU_WIDGET_SURVOL] as const;

/**
 * La peau complète, selon l'état. À passer à `cn()`.
 *
 * @param survol — `false` retire le survol : une surface non cliquable ne doit
 *                 pas réagir au passage de la souris.
 */
export function peauWidget(actif: boolean, survol = true): string[] {
  if (actif) return [...PEAU_WIDGET_BASE, ...PEAU_WIDGET_ACTIF];
  return [...PEAU_WIDGET_BASE, ...PEAU_WIDGET_REPOS, ...(survol ? [PEAU_WIDGET_SURVOL] : [])];
}

/**
 * Les deux décorations de l'état actif : la barre gauche et le filet du haut.
 *
 * ⚠ *`aria-hidden` : elles redisent en couleur ce que `aria-pressed` dit déjà.*
 */
export function DecorWidgetActif(): JSX.Element {
  return (
    <>
      <span
        className="pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-l-xl bg-brand-samsung-500"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-xl bg-gradient-to-r from-transparent via-brand-samsung-400/50 to-transparent"
        aria-hidden
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LA CARTE DE NOTIFICATION — **la peau des widgets, et l'état par la présence.**
 *
 * ⚠⚠ **LE VERT A ÉTÉ ESSAYÉ PUIS RETIRÉ, LE MÊME JOUR** *(Hkm, 10/09 : d'abord
 *    « en vert leger quand non lu », puis « **on enleve le vert, on garde les
 *    couleurs comme widgets, et a peine visible quand c'est lu** »)*. *Une
 *    couleur d'état de plus dans un écran qui en porte déjà quatre — rouge,
 *    orange, jaune, bleu — ajoutait une convention à apprendre. **L'état ne se
 *    dit plus par une teinte, il se dit par la PRÉSENCE** : une carte non lue a
 *    la surface pleine d'un widget, une carte lue s'efface.*
 *
 * ⚠ **« À PEINE VISIBLE » N'EST PAS « INVISIBLE ».** *L'opacité revient au
 *   survol : une carte lue reste lisible quand on la cherche, et cliquable
 *   pour revenir en non lue. L'effacer tout à fait aurait rendu la bascule
 *   inverse introuvable.*
 * ────────────────────────────────────────────────────────────────────────── */

/** ⚠ Non lue : exactement la surface d'un widget au repos. */
export const PEAU_NOTIF_NON_LUE = [...PEAU_WIDGET_REPOS, PEAU_WIDGET_SURVOL] as const;

/** ⚠ Lue : la même surface, effacée — et qui revient sous la souris. */
export const PEAU_NOTIF_LUE = [
  ...PEAU_WIDGET_REPOS, PEAU_WIDGET_SURVOL,
  'opacity-40 hover:opacity-90',
] as const;

/** La peau d'une carte de notification, selon son état de lecture. */
export function peauNotification(lue: boolean): string[] {
  return [
    FORME, ...CADRE_ET_FOCUS, 'cursor-pointer',
    ...(lue ? PEAU_NOTIF_LUE : PEAU_NOTIF_NON_LUE),
  ];
}
